import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import ReviewsTable from "./ReviewsTable";
import GetStartedButton from "./GetStartedButton";
import {
  nationalDebtRelief,
  freedomDebtRelief,
  unitedSettlement,
  accreditedDebtRelief,
  creditSaint,
  trustpilotLogo,
} from "@/images";
import { trackReviewsCTA } from "@/components/analytics/reviewsEvents";

// Map webDomain to company logo
const COMPANY_LOGOS = {
  "www.nationaldebtrelief.com": nationalDebtRelief,
  "freedomdebtrelief.com": freedomDebtRelief,
  "unitedsettlement.com": unitedSettlement,
  "www.accrediteddebtrelief.com": accreditedDebtRelief,
  "creditsaint.com": creditSaint,
};

// Fallback translations for when t is not available
const fallbackTranslations = {
  en: {
    "reviews.modal.title": "Customer Reviews",
    "reviews.modal.showingLatest": "Showing latest {{count}} reviews out of {{total}} from Trustpilot",
    "reviews.modal.showingVerified": "Showing verified 5-star reviews from Trustpilot",
    "reviews.modal.loading": "Loading reviews...",
    "reviews.table.rating": "Rating",
    "reviews.table.author": "Author",
    "reviews.table.review": "Review",
    "reviews.table.readMore": "Read More",
    "reviews.table.noReviews": "No reviews yet",
    "reviews.table.checkBackLater": "Check back later for user reviews",
    "reviews.table.verified": "Verified",
    "reviews.table.verifiedBy": "Verified by:",
    "reviews.table.from": "From:",
    "reviews.overlay.backToReviews": "Back to Reviews",
    "reviews.overlay.nextReview": "Next Review",
    "reviews.cta.getStarted": "Get Started Today!",
  },
  es: {
    "reviews.modal.title": "Reseñas de Clientes",
    "reviews.modal.showingLatest": "Mostrando las últimas {{count}} reseñas de {{total}} en Trustpilot",
    "reviews.modal.showingVerified": "Mostrando reseñas verificadas de 5 estrellas de Trustpilot",
    "reviews.modal.loading": "Cargando reseñas...",
    "reviews.table.rating": "Calificación",
    "reviews.table.author": "Autor",
    "reviews.table.review": "Reseña",
    "reviews.table.readMore": "Leer Más",
    "reviews.table.noReviews": "Aún no hay reseñas",
    "reviews.table.checkBackLater": "Vuelve más tarde para ver las reseñas de usuarios",
    "reviews.table.verified": "Verificado",
    "reviews.table.verifiedBy": "Verificado por:",
    "reviews.table.from": "De:",
    "reviews.overlay.backToReviews": "Volver a Reseñas",
    "reviews.overlay.nextReview": "Siguiente Reseña",
    "reviews.cta.getStarted": "¡Comienza Hoy!",
  },
};

// Create a fallback t function that handles interpolation
const createFallbackT = (lang = "en") => (key, options) => {
  const translations = fallbackTranslations[lang] || fallbackTranslations.en;
  let text = translations[key] || fallbackTranslations.en[key] || key;
  if (options) {
    Object.keys(options).forEach(optKey => {
      text = text.replace(`{{${optKey}}}`, options[optKey]);
    });
  }
  return text;
};

export default function ReviewsModal({
  open,
  onOpenChange,
  webDomain,
  affiliateLink,
  companyName,
  totalReviews,
  t: tProp,
}) {
  // Get language from context (works at root level)
  const { language } = useLanguage();
  // Use provided t function or fallback with current language
  const t = tProp || createFallbackT(language);
  const companyLogo = COMPANY_LOGOS[webDomain];
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && webDomain) {
      // Reset state and load reviews JSON file
      setLoading(true);
      setReviews([]);

      // Create AbortController for cleanup
      const abortController = new AbortController();

      // Fetch reviews from language-specific folder, fallback to English
      const reviewLang = language || "en";
      fetch(`/reviews/${reviewLang}/${webDomain}.json`, { signal: abortController.signal })
        .catch(() => {
          // Fallback to English if language-specific file doesn't exist
          return fetch(`/reviews/en/${webDomain}.json`, { signal: abortController.signal });
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Reviews not found");
          }
          return response.json();
        })
        .then((data) => {
          setReviews(data);
          setLoading(false);
        })
        .catch((error) => {
          // Don't update state if fetch was aborted
          if (error.name === "AbortError") {
            return;
          }
          console.error("Error loading reviews:", error);
          setReviews([]);
          setLoading(false);
        });

      // Cleanup function to abort fetch on unmount or dependency change
      return () => {
        abortController.abort();
      };
    } else if (!open) {
      // Reset state when modal closes
      setLoading(true);
      setReviews([]);
    }
  }, [open, webDomain, companyName, language]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleCTAClose = () => {
    trackReviewsCTA(companyName, "modal");
    onOpenChange(false);
  };

  const handleScroll = () => {
    // Placeholder for scroll tracking if needed
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col p-2 sm:p-6">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="pb-4 border-b border-border-light">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <h2 className="text-base sm:text-2xl font-bold text-text-primary">
                {t("reviews.modal.title")}
              </h2>
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-8 sm:h-[50px] max-w-[120px] sm:max-w-[200px] object-contain"
                />
              ) : (
                <span className="text-lg sm:text-2xl font-bold text-text-primary">
                  {companyName}
                </span>
              )}
              <img
                src={trustpilotLogo}
                alt="Trustpilot"
                className="h-6 sm:h-10 object-contain"
              />
            </div>
            <p className="text-sm text-text-secondary mt-1">
              {reviews.length > 0
                ? t("reviews.modal.showingLatest", { count: reviews.length, total: totalReviews || t("reviews.modal.showingVerified") })
                : t("reviews.modal.showingVerified")}
            </p>
          </div>

          {/* Reviews Table */}
          <div className="flex-1 overflow-hidden mt-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-text-secondary">{t("reviews.modal.loading")}</div>
              </div>
            ) : (
              <ReviewsTable
                reviews={reviews}
                onScroll={handleScroll}
                affiliateLink={affiliateLink}
                companyName={companyName}
                t={t}
                language={language}
              />
            )}
          </div>

          {/* Get Started Button */}
          <div className="pt-4 border-t border-border-light mt-4">
            <GetStartedButton
              affiliateLink={affiliateLink}
              onClose={handleCTAClose}
              t={t}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
